import { App, Browser, Device, Host, Release, Session, User } from "./Telemetry/Contracts";
import * as Utils from "./../Utils";
import TelemetryLoggerFactory from "./Telemetry/TelemetryLoggerFactory";
import FloodgateStorageProvider from "../FloodgateCore/FloodgateStorageProvider";
import { FileType } from "@ms-ofb/officefloodgatecore/dist/src/Api/IFloodgateStorageProvider";
export { EventIds } from "./Constants";
import { getDynamicSetting } from "../Configuration/DynamicConfiguration";
const {
  isNOU
} = Utils;
const NAMESPACE = "Office_Feedback";
let logger;
let env;
/**
 * Initialize the logging module
 * @param initOptionsCommon object contains common init options
 * @param appName app name
 * @param appVersion app version
 * @param logSessionId local SDK session id
 */
export function initialize(initOptionsCommon, appName, appVersion, logSessionId, samplingOption) {
  const telemetryGroup = initOptionsCommon.telemetryGroup ? initOptionsCommon.telemetryGroup : {};
  const webGroup = initOptionsCommon.webGroup ? initOptionsCommon.webGroup : {};
  logger = new Logger(TelemetryLoggerFactory.create(TokenManager.getTenantToken(initOptionsCommon.environment), NAMESPACE, new App(appName, appVersion), new Session(logSessionId), new Host(initOptionsCommon.appId.toString(), initOptionsCommon.originalLocale, initOptionsCommon.sessionId, initOptionsCommon.build), new Release(telemetryGroup.audienceGroup), new Browser(webGroup.browser, webGroup.browserVersion), new Device(telemetryGroup.deviceId), new User(telemetryGroup.loggableUserId ? telemetryGroup.loggableUserId : initOptionsCommon.cid, telemetryGroup.tenantId)), samplingOption, initOptionsCommon);
  env = initOptionsCommon.environment;
}
/**
 * Get the logger object
 */
export function getLogger() {
  return logger;
}
;
;
/**
 * Class representing a logger for the feedback SDK
 */
export class Logger {
  constructor(telemetryLogger, samplingOptions, initOptionsCommon) {
    this.SAMPLING_RESULT = Math.random();
    if (!telemetryLogger) {
      throw new Error("telemetryLogger must not be null");
    }
    this.telemetryLogger = telemetryLogger;
    this.eventSampling = samplingOptions;
    this.initOptionsCommon = initOptionsCommon;
  }
  /**
   * Log an event
   * @param eventId event Id
   * @param logSeverity the log level severity for the message
   * @param customProperties custom properties to add to the log
   */
  logEvent(eventId, logSeverity, customProperties) {
    if (!eventId) {
      throw new Error("eventId must not be null");
    }
    const sampleRate = this.getEventSamplingRate(eventId.name);
    if (isNOU(logSeverity) || logSeverity === 0 /* None */) {
      if (env === 0 /* Production */) {
        const errorMessage = "logSeverity must not be null or none";
        if (this.isConsoleLogEnabled && console) {
          // tslint:disable:no-console
          console.log("Floodgate event: ", Logger.EVENT_NAME, errorMessage);
        }
        this.telemetryLogger.logEvent(Logger.EVENT_NAME, {
          ErrorMessage: errorMessage
        }, sampleRate);
      }
      return false;
    }
    customProperties = customProperties || {};
    customProperties[Logger.EVENT_ID] = eventId.name;
    if (this.isLoggingEnabled(logSeverity, sampleRate) || this.isEventTelemetryEnabled(eventId.name)) {
      this.telemetryLogger.logEvent(Logger.EVENT_NAME, customProperties, sampleRate);
    }
    if (this.isConsoleLogEnabled && console) {
      // tslint:disable:no-console
      console.log("Floodgate event: ", Logger.EVENT_NAME, logSeverity, customProperties);
    }
    return true;
  }
  logLocal(logSeverity) {
    if (this.isLoggingEnabled(logSeverity) && this.isConsoleLogEnabled && console) {
      for (var _len = arguments.length, others = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        others[_key - 1] = arguments[_key];
      }
      // tslint:disable:no-console
      console.log("LPP FG: ", logSeverity, others);
    }
  }
  getUserSamplingRate() {
    return this.SAMPLING_RESULT;
  }
  isEventTelemetryEnabled(eventName) {
    var _a, _b, _c, _d;
    try {
      if (!this.dynamicLogSettings) {
        return false;
      }
      const appId = (_b = (_a = this.initOptionsCommon) === null || _a === void 0 ? void 0 : _a.appId) === null || _b === void 0 ? void 0 : _b.toString();
      const locale = (_d = (_c = this.initOptionsCommon) === null || _c === void 0 ? void 0 : _c.originalLocale) === null || _d === void 0 ? void 0 : _d.toLowerCase();
      const settingName = `content_${eventName}`;
      // try settings from more specific to generic order
      const overrideSettings = this.dynamicLogSettings[`${settingName}_${appId}_${locale}`] || this.dynamicLogSettings[`${settingName}_${appId}`] || this.dynamicLogSettings[`${settingName}_${locale}`] || this.dynamicLogSettings[settingName];
      return overrideSettings === null || overrideSettings === void 0 ? void 0 : overrideSettings.isEnabled;
    } catch (e) {
      if (this.isConsoleLogEnabled) {
        // tslint:disable:no-console
        console.log("FG: Error checking for dynamic log settings: ", e);
      }
    }
    return false;
  }
  /**
   * This method enables logging only if the log severity of the event is less than or equal to the
   * current log level set and within the sample rate. For example, if the log severity of the event is
   * LogLevel.Error and the current log level is set as LogLevel.Critical, then isLoggingEnabled will
   * return false since the condition LogLevel.Error <= LogLevel.Critical is false. Because, the log severity
   * follows the order defined in the LogLevel enum (None < Critical < Error < Info). The sample rate is the
   * percentage of users allow to send the event back. If the random genarated number is less than the sample rate
   * and the sample rate is not full blocking (0 value)
   * If no current log level is set, then the default log level is set as LogLevel.Error so that all events
   * marked with critical and error log severity get logged.
   * @param inputLogLevel the log severity of the event
   */
  isLoggingEnabled(inputLogLevel) {
    let sampleRate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    if (isNOU(this.logLevel)) {
      const currentLogLevel = this.getCurrentLogLevel();
      this.logLevel = isNOU(currentLogLevel) ? 2 /* Error */ : currentLogLevel;
    }
    return inputLogLevel <= this.logLevel && this.SAMPLING_RESULT <= sampleRate && sampleRate !== 0;
  }
  /**
   * This method returns the current log level if it is set in either url query parameters or floodgate local storage.
   * Following table summarizes whether the log event calls with a log severity will log the events or not according
   * to the current log level set. For example, the first row in the table demonstrates that when the current log level
   * enabled is critical, then the log event will log the events only if the log severity is critical.
   *
   * Current Log Level Enabled | LogSeverity: Critical	| LogSeverity: Error	| LogSeverity: Info
   * --------------------------------------------------------------------------------------------
   * Critical Enabled			 | yes      				| no    			 	| no
   * Error Enabled       		 | yes      				| yes   			 	| no
   * Info Enabled        		 | yes      				| yes   			 	| yes
   * None Enabled        		 | no       				| no    			 	| no
   *
   * Console logging will be enabled when the current log level is set through the url query parameters or
   * when the url query parameter "obfconsolelog" is explicitly set to true.
   */
  getCurrentLogLevel() {
    try {
      // Check if console log is enabled in local browser session with query parameter overrides
      const urlParams = typeof URLSearchParams !== "undefined" && new URLSearchParams(window.location.search) || undefined;
      let consoleLogParam = urlParams && urlParams.get("obfconsolelog");
      if (!consoleLogParam) {
        // for getting iframe host url - eg: WAC
        const documentReferrer = typeof document !== "undefined" && document.referrer;
        consoleLogParam = documentReferrer && documentReferrer.indexOf("obfconsolelog") !== -1;
      }
      if (consoleLogParam) {
        this.isConsoleLogEnabled = true;
      }
      // Check if log level is set in local browser session with query parameter overrides
      const logLevelParam = urlParams && urlParams.get("obfloglevel");
      if (!isNOU(logLevelParam)) {
        this.isConsoleLogEnabled = true;
        return parseInt(logLevelParam, 10);
      }
      // Check if log level is set in floodgate local storage (manually or with remotely fetched settings)
      if (FloodgateStorageProvider.isStorageAvailable()) {
        const floodgateStorageProvider = new FloodgateStorageProvider();
        let tempStorageItem = floodgateStorageProvider.read(FileType.LogLevelSettings);
        if (!tempStorageItem) {
          tempStorageItem = getDynamicSetting("LogLevelSettings" /* logLevelSettings */, "");
        }
        const settingsObj = tempStorageItem && this.parseObject(tempStorageItem);
        if (settingsObj) {
          this.dynamicLogSettings = settingsObj;
          let logSettings = settingsObj.content;
          if (this.initOptionsCommon) {
            const appId = this.initOptionsCommon.appId && this.initOptionsCommon.appId.toString();
            const locale = this.initOptionsCommon.originalLocale && this.initOptionsCommon.originalLocale.toLowerCase();
            // log settings from more specific to generic order
            logSettings = settingsObj[`content_${appId}_${locale}`] || settingsObj[`content_${appId}`] || settingsObj[`content_${locale}`] || settingsObj[`content`];
          }
          if (logSettings) {
            if (logSettings.consoleLog) {
              this.isConsoleLogEnabled = true;
            }
            return logSettings.logLevel;
          }
        }
      }
    } catch (e) {
      if (this.isConsoleLogEnabled) {
        // tslint:disable:no-console
        console.log("Error while getting the current log level: ", e);
      }
      return undefined;
    }
    return null;
  }
  parseObject(tempItem) {
    try {
      return JSON.parse(tempItem);
    } catch (e) {
      if (this.isConsoleLogEnabled) {
        // tslint:disable:no-console
        console.log("Error while parsing the json string for log level: ", e);
      }
      return null;
    }
  }
  getEventSamplingRate(eventId) {
    let sampleRate = 1;
    if (this.eventSampling.event) {
      // If the event is init with sample rate
      // check the sampling result against the sample rate
      if (eventId in this.eventSampling.event) {
        sampleRate = this.eventSampling.event[eventId];
      }
    }
    return sampleRate;
  }
}
Logger.EVENT_NAME = "SDK";
Logger.EVENT_ID = "EventId";
class TokenManager {
  static getTenantToken(environment) {
    if (environment === 0 /* Production */) {
      return TokenManager.TENANT_TOKEN_PRODUCTION;
    } else {
      return TokenManager.TENANT_TOKEN_PRE_PRODUCTION;
    }
  }
}
TokenManager.TENANT_TOKEN_PRODUCTION = "d79e824386c4441cb8c1d4ae15690526-bd443309-5494-444a-aba9-0af9eef99f84-7360"; // "Office Feedback" Prod Aria tenant
TokenManager.TENANT_TOKEN_PRE_PRODUCTION = "2bf6a2ffddca4a80a892a0b182132961-625cb102-8b0c-480e-af53-92e48695d08d-7736"; // "Office Feedback" Sandbox Aria tenant